import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { 
  FileText, Download, Users, Building2, Calendar, TrendingUp, 
  ChevronLeft, Filter, BarChart2, FileSpreadsheet, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth } from 'date-fns';

const AdminReports = () => {
  const [activeReport, setActiveReport] = useState('participants');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [townFilter, setTownFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [groupBy, setGroupBy] = useState('month');
  
  // Filter options
  const [towns, setTowns] = useState([]);
  const [partners, setPartners] = useState([]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [filterRes, partnerRes] = await Promise.all([
        axios.get(`${API}/opportunities/meta/filter-options`),
        axios.get(`${API}/admin/partner-organizations`)
      ]);
      setTowns(filterRes.data.towns || []);
      setPartners(partnerRes.data || []);
    } catch (err) {
      console.error('fetchFilterOptions failed', err);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/reports/${activeReport}`;
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      if (activeReport === 'participants' || activeReport === 'opportunities') {
        if (townFilter) params.append('town', townFilter);
        if (partnerFilter) params.append('partner_id', partnerFilter);
      }
      
      if (activeReport === 'attendance-trends') {
        params.append('group_by', groupBy);
      }
      
      const response = await axios.get(`${url}?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [activeReport, startDate, endDate, townFilter, partnerFilter, groupBy]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExportCSV = async () => {
    try {
      let url = `${API}/admin/reports/${activeReport}/csv`;
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      if (activeReport === 'participants' || activeReport === 'opportunities') {
        if (townFilter) params.append('town', townFilter);
        if (partnerFilter) params.append('partner_id', partnerFilter);
      }
      
      if (activeReport === 'attendance-trends') {
        params.append('group_by', groupBy);
      }
      
      const response = await axios.get(`${url}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${activeReport}_report_${startDate}_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportBoardPDF = async () => {
    try {
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/admin/reports/board-summary/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/html' });
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Open in new window for printing
      const printWindow = window.open(downloadUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Board Summary ready for print');
    } catch (error) {
      toast.error('Failed to generate Board Summary');
    }
  };

  const handleExportPartnerPDF = async (partnerId) => {
    try {
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/admin/reports/partner-summary/pdf/${partnerId}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/html' });
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const printWindow = window.open(downloadUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Partner Summary ready for print');
    } catch (error) {
      toast.error('Failed to generate Partner Summary');
    }
  };

  const setQuickDateRange = (range) => {
    const now = new Date();
    let start;
    
    switch(range) {
      case '7':
        start = subDays(now, 7);
        break;
      case '30':
        start = subDays(now, 30);
        break;
      case '90':
        start = subDays(now, 90);
        break;
      case 'month':
        start = startOfMonth(now);
        break;
      default:
        start = subDays(now, 30);
    }
    
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(now, 'yyyy-MM-dd'));
  };

  const reportTabs = [
    { id: 'participants', label: 'By Participant', icon: Users },
    { id: 'partners', label: 'By Partner', icon: Building2 },
    { id: 'opportunities', label: 'By Opportunity', icon: Calendar },
    { id: 'attendance-trends', label: 'Trends', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen" data-testid="admin-reports">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin" className="text-[#8B2E3C] hover:underline flex items-center gap-1 mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-2">Advanced Reports</h1>
              <p className="text-base text-[#666666]">Generate board-ready and partner-ready reports</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportBoardPDF}
                className="btn-primary flex items-center gap-2"
                data-testid="export-board-pdf"
              >
                <Printer className="w-4 h-4" />
                Board Summary PDF
              </button>
            </div>
          </div>
        </div>

        {/* Report Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {reportTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveReport(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                  activeReport === tab.id
                    ? 'bg-[#8B2E3C] text-white'
                    : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
                }`}
                data-testid={`report-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="card mb-6" data-testid="report-filters">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#8B2E3C]" />
            <h3 className="font-bold text-[#222222]">Filters</h3>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field"
                data-testid="filter-end-date"
              />
            </div>
            
            {/* Town filter (only for participants and opportunities) */}
            {(activeReport === 'participants' || activeReport === 'opportunities') && (
              <div>
                <label className="block text-sm font-medium mb-1">Town</label>
                <select
                  value={townFilter}
                  onChange={(e) => setTownFilter(e.target.value)}
                  className="input-field"
                  data-testid="filter-town"
                >
                  <option value="">All Towns</option>
                  {towns.map(town => (
                    <option key={town} value={town}>{town}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Partner filter (only for participants and opportunities) */}
            {(activeReport === 'participants' || activeReport === 'opportunities') && (
              <div>
                <label className="block text-sm font-medium mb-1">Partner</label>
                <select
                  value={partnerFilter}
                  onChange={(e) => setPartnerFilter(e.target.value)}
                  className="input-field"
                  data-testid="filter-partner"
                >
                  <option value="">All Partners</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>{partner.org_name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Group by (only for trends) */}
            {activeReport === 'attendance-trends' && (
              <div>
                <label className="block text-sm font-medium mb-1">Group By</label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="input-field"
                  data-testid="filter-group-by"
                >
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            )}
          </div>
          
          {/* Quick Date Buttons */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-[#666666] mr-2">Quick:</span>
            <button
              onClick={() => setQuickDateRange('7')}
              className="px-3 py-1 text-sm rounded-lg bg-[#FBF4EA] text-[#222222] hover:bg-[#E9DDCF]"
            >
              Last 7 days
            </button>
            <button
              onClick={() => setQuickDateRange('30')}
              className="px-3 py-1 text-sm rounded-lg bg-[#FBF4EA] text-[#222222] hover:bg-[#E9DDCF]"
            >
              Last 30 days
            </button>
            <button
              onClick={() => setQuickDateRange('90')}
              className="px-3 py-1 text-sm rounded-lg bg-[#FBF4EA] text-[#222222] hover:bg-[#E9DDCF]"
            >
              Last 90 days
            </button>
            <button
              onClick={() => setQuickDateRange('month')}
              className="px-3 py-1 text-sm rounded-lg bg-[#FBF4EA] text-[#222222] hover:bg-[#E9DDCF]"
            >
              This month
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {reportData?.summary && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="report-summary">
            <div className="card bg-[#FBF4EA]">
              <div className="text-sm text-[#666666]">Total Opportunities</div>
              <div className="text-2xl font-bold text-[#8B2E3C]">{reportData.summary.total_opportunities}</div>
            </div>
            <div className="card bg-[#FBF4EA]">
              <div className="text-sm text-[#666666]">Total Signups</div>
              <div className="text-2xl font-bold text-[#C9A227]">{reportData.summary.total_signups}</div>
            </div>
            <div className="card bg-[#FBF4EA]">
              <div className="text-sm text-[#666666]">Attended Hours</div>
              <div className="text-2xl font-bold text-green-600">{reportData.summary.total_attended_hours}h</div>
            </div>
            <div className="card bg-[#FBF4EA]">
              <div className="text-sm text-[#666666]">No-Shows</div>
              <div className="text-2xl font-bold text-red-600">
                {reportData.summary.no_shows_count} ({reportData.summary.no_show_percentage}%)
              </div>
            </div>
          </div>
        )}

        {/* Export Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={handleExportCSV}
            className="btn-outline flex items-center gap-2"
            data-testid="export-csv"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Report Content */}
        {loading ? (
          <div className="card text-center py-12">
            <div className="text-[#666666]">Loading report...</div>
          </div>
        ) : reportData?.data?.length === 0 ? (
          <div className="card text-center py-12">
            <div className="text-[#666666]">No data found for the selected filters.</div>
          </div>
        ) : (
          <>
            {/* Participants Report */}
            {activeReport === 'participants' && reportData?.data && (
              <div className="card overflow-x-auto" data-testid="participants-report-table">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="border-b-2 border-[#E9DDCF]">
                      <th className="text-left py-3 px-4 font-semibold">Participant</th>
                      <th className="text-left py-3 px-4 font-semibold">Email</th>
                      <th className="text-left py-3 px-4 font-semibold">Town</th>
                      <th className="text-left py-3 px-4 font-semibold">Partner</th>
                      <th className="text-right py-3 px-4 font-semibold">Hours</th>
                      <th className="text-right py-3 px-4 font-semibold">Attended</th>
                      <th className="text-right py-3 px-4 font-semibold">No-Shows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data.map((item, index) => (
                      <tr key={item.participant_id || index} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                        <td className="py-3 px-4 font-medium">{item.participant_name}</td>
                        <td className="py-3 px-4 text-sm text-[#666666]">{item.participant_email}</td>
                        <td className="py-3 px-4 text-sm">{item.town || '-'}</td>
                        <td className="py-3 px-4 text-sm">{item.partner_name || '-'}</td>
                        <td className="py-3 px-4 text-right font-bold text-[#8B2E3C]">{item.total_hours}h</td>
                        <td className="py-3 px-4 text-right text-green-600">{item.attended_count}</td>
                        <td className="py-3 px-4 text-right text-red-600">{item.no_show_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Partners Report */}
            {activeReport === 'partners' && reportData?.data && (
              <div className="card overflow-x-auto" data-testid="partners-report-table">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b-2 border-[#E9DDCF]">
                      <th className="text-left py-3 px-4 font-semibold">Partner</th>
                      <th className="text-right py-3 px-4 font-semibold">Hours</th>
                      <th className="text-right py-3 px-4 font-semibold">Volunteers</th>
                      <th className="text-right py-3 px-4 font-semibold">Opportunities</th>
                      <th className="text-right py-3 px-4 font-semibold">Attended</th>
                      <th className="text-right py-3 px-4 font-semibold">No-Shows</th>
                      <th className="text-right py-3 px-4 font-semibold">No-Show %</th>
                      <th className="text-center py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data.map((item, index) => (
                      <tr key={item.partner_id || index} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                        <td className="py-3 px-4 font-medium">{item.partner_name}</td>
                        <td className="py-3 px-4 text-right font-bold text-[#8B2E3C]">{item.total_hours}h</td>
                        <td className="py-3 px-4 text-right">{item.volunteer_count}</td>
                        <td className="py-3 px-4 text-right">{item.opportunity_count}</td>
                        <td className="py-3 px-4 text-right text-green-600">{item.attended_count}</td>
                        <td className="py-3 px-4 text-right text-red-600">{item.no_show_count}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={item.no_show_percentage > 20 ? 'text-red-600 font-bold' : ''}>
                            {item.no_show_percentage}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleExportPartnerPDF(item.partner_id)}
                            className="text-[#8B2E3C] hover:underline text-sm flex items-center gap-1 justify-center"
                            data-testid={`partner-pdf-${item.partner_id}`}
                          >
                            <Printer className="w-3 h-3" />
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Opportunities Report */}
            {activeReport === 'opportunities' && reportData?.data && (
              <div className="card overflow-x-auto" data-testid="opportunities-report-table">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr className="border-b-2 border-[#E9DDCF]">
                      <th className="text-left py-3 px-4 font-semibold">Opportunity</th>
                      <th className="text-left py-3 px-4 font-semibold">Date</th>
                      <th className="text-left py-3 px-4 font-semibold">Partner</th>
                      <th className="text-left py-3 px-4 font-semibold">Town</th>
                      <th className="text-right py-3 px-4 font-semibold">Capacity</th>
                      <th className="text-right py-3 px-4 font-semibold">Signups</th>
                      <th className="text-right py-3 px-4 font-semibold">Attended</th>
                      <th className="text-right py-3 px-4 font-semibold">No-Shows</th>
                      <th className="text-right py-3 px-4 font-semibold">Hours</th>
                      <th className="text-right py-3 px-4 font-semibold">Fill Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data.map((item, index) => (
                      <tr key={item.opportunity_id || index} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                        <td className="py-3 px-4 font-medium">{item.opportunity_title}</td>
                        <td className="py-3 px-4 text-sm">{item.opportunity_date}</td>
                        <td className="py-3 px-4 text-sm">{item.partner_name || '-'}</td>
                        <td className="py-3 px-4 text-sm">{item.town || '-'}</td>
                        <td className="py-3 px-4 text-right">{item.capacity}</td>
                        <td className="py-3 px-4 text-right">{item.signups_count}</td>
                        <td className="py-3 px-4 text-right text-green-600">{item.attended_count}</td>
                        <td className="py-3 px-4 text-right text-red-600">{item.no_show_count}</td>
                        <td className="py-3 px-4 text-right font-bold text-[#8B2E3C]">{item.total_hours}h</td>
                        <td className="py-3 px-4 text-right">
                          <span className={item.fill_rate >= 80 ? 'text-green-600' : item.fill_rate >= 50 ? 'text-[#C9A227]' : 'text-red-600'}>
                            {item.fill_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Attendance Trends Report */}
            {activeReport === 'attendance-trends' && reportData?.data && (
              <div className="card" data-testid="trends-report">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b-2 border-[#E9DDCF]">
                        <th className="text-left py-3 px-4 font-semibold">Period</th>
                        <th className="text-right py-3 px-4 font-semibold">Attended</th>
                        <th className="text-right py-3 px-4 font-semibold">No-Shows</th>
                        <th className="text-right py-3 px-4 font-semibold">Late Cancels</th>
                        <th className="text-right py-3 px-4 font-semibold">Total</th>
                        <th className="text-right py-3 px-4 font-semibold">No-Show Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.map((item, index) => (
                        <tr key={item.period || index} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                          <td className="py-3 px-4 font-medium">{item.period}</td>
                          <td className="py-3 px-4 text-right text-green-600">{item.attended}</td>
                          <td className="py-3 px-4 text-right text-red-600">{item.no_shows}</td>
                          <td className="py-3 px-4 text-right text-[#C9A227]">{item.late_cancels}</td>
                          <td className="py-3 px-4 text-right font-bold">{item.total}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={item.no_show_rate > 20 ? 'text-red-600 font-bold' : ''}>
                              {item.no_show_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Simple Visual Chart */}
                {reportData.data.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-[#E9DDCF]">
                    <h4 className="font-bold text-[#222222] mb-4 flex items-center gap-2">
                      <BarChart2 className="w-5 h-5" />
                      Attendance Trend Visualization
                    </h4>
                    <div className="flex items-end gap-2 h-40">
                      {reportData.data.map((item, index) => {
                        const maxTotal = Math.max(...reportData.data.map(d => d.total), 1);
                        const height = (item.total / maxTotal) * 100;
                        const attendedHeight = (item.attended / (item.total || 1)) * 100;
                        
                        return (
                          <div key={item.period || index} className="flex-1 flex flex-col items-center">
                            <div 
                              className="w-full rounded-t relative overflow-hidden"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                            >
                              <div 
                                className="absolute bottom-0 w-full bg-green-500"
                                style={{ height: `${attendedHeight}%` }}
                              />
                              <div 
                                className="absolute top-0 w-full bg-red-400"
                                style={{ height: `${100 - attendedHeight}%` }}
                              />
                            </div>
                            <div className="text-xs text-[#666666] mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                              {item.period}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-4 mt-8 justify-center text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded" />
                        <span>Attended</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-400 rounded" />
                        <span>No-Shows/Cancels</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Generated timestamp */}
        {reportData?.summary && (
          <div className="mt-4 text-sm text-[#666666] text-center">
            Report generated: {format(new Date(reportData.summary.generated_at), 'MMM d, yyyy h:mm a')}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
